(() => {
  const instances = new WeakMap();
  const init = root => {
    if (instances.has(root)) return;
    const tabs = [...root.querySelectorAll('[data-hx-tab]')];
    const panels = [...root.querySelectorAll('[data-hx-panel]')];
    const cleanups = [];
    const select = (index, focus = false) => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel, i) => {
        const active = i === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });
      root.querySelectorAll('[data-hx-story-link]').forEach((link, i) => link.classList.toggle('is-active', i === index));
      if (focus) tabs[index]?.focus();
    };
    const listen = (element, type, handler) => {
      element.addEventListener(type, handler, type === 'scroll' || type === 'pointermove' ? { passive: true } : undefined);
      cleanups.push(() => element.removeEventListener(type, handler));
    };
    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => select(index));
      listen(tab, 'keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        select(next, true);
      });
    });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let observer;
    const showAll = () => {
      observer?.disconnect();
      root.querySelectorAll('[data-hx-reveal]').forEach(element => element.classList.add('is-visible'));
    };
    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const reveals = [...root.querySelectorAll('[data-hx-reveal]')];
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px 24px 0px' });
      root.dataset.hxEnhanced = 'true';
      reveals.forEach(element => observer.observe(element));
      cleanups.push(() => observer.disconnect());
      listen(reducedMotion, 'change', event => { if (event.matches) showAll(); });
    }
    // One requested frame per input event; no continuous render loop.
    const hero = root.querySelector('[data-hx-hero]');
    const nativeHeader = document.querySelector('.section-header');
    const parallaxElements = [...root.querySelectorAll('[data-hx-parallax]')];
    const motionZones = [...root.querySelectorAll('[data-hx-motion-zone]')];
    const storySections = [...root.querySelectorAll('[data-hx-product-story]')];
    const activeParallax = new Set();
    let rootVisible = true;
    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const coarsePointer = window.matchMedia('(pointer: coarse)');
    const updateFrame = () => {
      frame = 0;
      if (!rootVisible || document.hidden) return;
      const bounds = root.getBoundingClientRect();
      const height = window.innerHeight;
      const progress = Math.max(0, Math.min(1, -bounds.top / Math.max(1, bounds.height - height)));
      root.style.setProperty('--hx-page-progress', String(progress));
      const headerBottom = nativeHeader?.getBoundingClientRect().bottom || 0;
      root.style.setProperty('--hx-header-height', Math.max(0, Math.min(180, headerBottom)) + 'px');
      if (reducedMotion.matches) return;
      activeParallax.forEach(element => {
        const rect = element.getBoundingClientRect();
        const distance = Math.max(-1, Math.min(1, (height / 2 - rect.top - rect.height / 2) / height));
        element.style.setProperty('--hx-parallax', (distance * (coarsePointer.matches ? 10 : 22)).toFixed(2) + 'px');
      });
      if (hero && !coarsePointer.matches) {
        hero.style.setProperty('--hx-pointer-x', pointerX.toFixed(2) + 'px');
        hero.style.setProperty('--hx-pointer-y', pointerY.toFixed(2) + 'px');
      }
    };
    const requestFrame = () => {
      if (!frame && rootVisible && !document.hidden) frame = window.requestAnimationFrame(updateFrame);
    };
    if ('IntersectionObserver' in window) {
      const visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === root) rootVisible = entry.isIntersecting;
          if (entry.target.hasAttribute('data-hx-motion-zone')) entry.target.classList.toggle('is-in-view', entry.isIntersecting);
          if (entry.target.hasAttribute('data-hx-parallax')) {
            if (entry.isIntersecting) activeParallax.add(entry.target);
            else activeParallax.delete(entry.target);
          }
        });
        requestFrame();
      }, { threshold: 0 });
      [root, ...motionZones, ...parallaxElements].forEach(element => visibility.observe(element));
      cleanups.push(() => visibility.disconnect());
      const stories = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          root.querySelectorAll('[data-hx-story-link]').forEach(link => link.classList.toggle('is-active', link.dataset.hxStoryLink === entry.target.dataset.hxProductStory));
        });
      }, { rootMargin: '-30% 0px -40% 0px', threshold: 0 });
      storySections.forEach(element => stories.observe(element));
      cleanups.push(() => stories.disconnect());
    }
    listen(window, 'scroll', requestFrame);
    listen(window, 'resize', requestFrame);
    // Re-measure after the theme's sticky header finishes sliding into view.
    if (nativeHeader) listen(nativeHeader, 'transitionend', requestFrame);
    listen(document, 'visibilitychange', () => {
      if (document.hidden && frame) { window.cancelAnimationFrame(frame); frame = 0; }
      else requestFrame();
    });
    listen(reducedMotion, 'change', event => {
      if (event.matches) {
        parallaxElements.forEach(element => element.style.setProperty('--hx-parallax', '0px'));
        hero?.style.setProperty('--hx-pointer-x', '0px');
        hero?.style.setProperty('--hx-pointer-y', '0px');
      }
      requestFrame();
    });
    if (hero) {
      listen(hero, 'pointermove', event => {
        if (reducedMotion.matches || coarsePointer.matches || event.pointerType === 'touch') return;
        const rect = hero.getBoundingClientRect();
        pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
        pointerY = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
        requestFrame();
      });
      listen(hero, 'pointerleave', () => { pointerX = 0; pointerY = 0; requestFrame(); });
    }
    cleanups.push(() => { if (frame) window.cancelAnimationFrame(frame); });
    requestFrame();
    const dispose = () => {
      cleanups.forEach(cleanup => cleanup());
      instances.delete(root);
    };
    instances.set(root, dispose);
  };
  const scan = (scope = document) => {
    if (scope.matches?.('[data-hx-home]')) init(scope);
    scope.querySelectorAll('[data-hx-home]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-hx-home]').forEach(root => instances.get(root)?.());
  });
  document.addEventListener('shopify:block:select', event => {
    const block = event.target.closest('[data-hx-reveal]');
    block?.classList.add('is-visible');
  });
})();
